// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyProduct } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    product: TyProduct;
    canEdit?: boolean;
    onMore?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onToggleActive?: () => void;
    onToggleHidden?: () => void;
    togglingActive?: boolean;
    togglingHidden?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

export default function ProductCard({ product, canEdit = true, onMore, onToggleActive, onToggleHidden, togglingActive, togglingHidden }: Props) {
    const t = useTranslation();

    const activeDiscount = product.discount?.active ? product.discount : null;

    const discountLabel = activeDiscount
        ? activeDiscount.type === "percentage"
            ? `${t.menu.discount} ${activeDiscount.amount}%`
            : `${t.menu.discount} ${activeDiscount.amount}`
        : null;

    return (
        <div className={`product-card${!product.active ? " product-card--inactive" : ""}${!canEdit ? " product-card--view-only" : ""}`}>
            <div className="product-card-inner">
                {/* DOM order: image first = inline-start (RIGHT in RTL) */}
                <div className="product-card-image">
                    {product.pictures[0] ? (
                        <img src={product.pictures[0]} alt={product.name} className="product-card-image-img" />
                    ) : (
                        <div className="product-card-image-placeholder">
                            <img src="/icons/category-icon.svg" alt="" width={28} height={28} />
                        </div>
                    )}
                </div>

                <div className="product-card-info">
                    <p className="product-card-name">{product.name}</p>
                    <p className="product-card-description">{product.description}</p>
                    <div className="product-card-prices">
                        {product.prices.map((price) => (
                            <div key={price._id} className="product-card-price-chip">
                                {price.showLabel && <span className="product-card-price-label">{price.label}:</span>}
                                <span className="product-card-price-amount">{price.amount}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {discountLabel && (
                    <div className="product-card-ribbon" aria-label={discountLabel}>
                        <div className="product-card-ribbon-band">
                            <span className="product-card-ribbon-text">{discountLabel}</span>
                        </div>
                    </div>
                )}
            </div>

            {canEdit && (
                <div className="product-card-footer">
                    {/* DOM order: first child = inline-start (RIGHT in RTL) */}
                    {/* Available = active — toggling this DOES dim the card when off */}
                    <button
                        type="button"
                        className={`product-card-toggle-pill${togglingActive ? " product-card-toggle-pill--loading" : ""}`}
                        onClick={onToggleActive}
                        disabled={togglingActive || !onToggleActive}
                    >
                        <span className="product-card-toggle-label">{t.menu.availableLabel}</span>
                        <div className={`product-card-toggle${product.active ? " product-card-toggle--on" : ""}`} />
                    </button>
                    {/* Visible = not hidden — toggling this does NOT affect card opacity */}
                    <button
                        type="button"
                        className={`product-card-toggle-pill${togglingHidden ? " product-card-toggle-pill--loading" : ""}`}
                        onClick={onToggleHidden}
                        disabled={togglingHidden || !onToggleHidden}
                    >
                        <span className="product-card-toggle-label">{t.menu.visibleLabel}</span>
                        <div className={`product-card-toggle${product.hidden ? " product-card-toggle--on" : ""}`} />
                    </button>
                    <button type="button" className="product-card-more-btn" onClick={onMore}>
                        <img src="/icons/more-icon.svg" alt="" width={16} height={16} />
                    </button>
                </div>
            )}
        </div>
    );
}
